import {startCase} from 'lodash';
// eslint-disable-next-line webflow/no-dangerous-imports
import {wrapAddPanelIcon} from '@packages/systems/ui/shared-ui/wrapAddPanelIcon';
import {
  Atom,
  Expressions,
  Plugins,
  Result,
  TypeImplementation,
  withMeta,
  withSystem,
  type AtomConfig,
} from '@packages/systems/wfdl';
import * as React from 'react';
import {
  qualify,
  iconName,
  pluginName,
  typeName,
} from '@packages/systems/wfdl-Qualified';
import ListIcon from './icons/element-list';
import RecordIcon from './icons/Record';
import {PrimitiveTypes} from './PrimitiveTypes';
import {DecimalNumberSelectorType} from './NumberPrimitiveType';
import {
  CustomElementIcon,
  AddPanelCustomElement64Icon,
} from '@packages/systems/spring';
import {type ContextualExpression} from '@packages/utilities/contextual-expression';
import {sanitizeProp} from '@packages/utilities/react-sanitize';
import {getChildren} from '@packages/utilities/data-utils';
import {
  ExpressionElement,
  functionOf,
  TElement,
  TList,
  TNumber,
  TRecord,
  TText,
  TTypeConstructor,
  TListOfElement,
} from '@packages/systems/wfdl-ast';
import {clamp} from '@packages/systems/core/utils/NumberUtils';

const CustomMElementAddPanelIcon = wrapAddPanelIcon(
  AddPanelCustomElement64Icon
);

const noRender = (_: {
  dispatch: (arg1?: any) => void;
  expr: ContextualExpression;
}) => null;

const typeWithDefaultEmptyArray = TypeImplementation.Custom({
  render: noRender,
  system: {defaultValue: []},
});

const Dummy = Atom.create({
  render: Atom.createRender(() => {
    throw new Error(
      'BuiltinPluginFactory needs a real DOM and DangerousDOM definition.'
    );
  }),
  showEmpty: false,
});

function attrsDOMReducer(
  memo: Record<string, string>,
  {
    name,
    value,
  }: {
    name: string;
    value: string;
  }
) {
  if (name === 'is' || name === 'dangerouslySetInnerHTML') return memo;

  // React expects style to be an object; the work around is to pass our style
  // string as STYLE instead.
  if (name === 'style' && typeof value === 'string') {
    memo.STYLE = value;
  } else {
    memo[name] = sanitizeProp(name, value);
  }
  return memo;
}

const attributesType = withSystem(
  {label: 'Attributes'},
  TList(
    withMeta(
      {
        renderSummaryKeys: [
          {key: 'name'},
          {key: 'value', addons: {prefix: ' = '}},
        ],
      },
      TRecord({
        name: withSystem({isBindable: true}, TText),
        value: withSystem({isBindable: true}, TText),
      })
    )
  )
);

const DOMForTest = Atom.create({
  render: Atom.createRender(
    ({data, generateAttributes, attributes, classes, renderChild}) => {
      // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
      const Tag = data.tag;

      if (Tag === undefined) {
        throw new Error(
          'Builtin DOM `tag` is undefined. Make sure to pass in a valid `tag` to the DOM `data` Record.'
        );
      }

      const props = {
        ...generateAttributes(attributes, classes),
        // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
        ...data.attributes.reduce(attrsDOMReducer, {}),
      };

      const children = getChildren(data);
      if (!children.length) {
        return <Tag {...props} />;
      }

      return (
        <Tag {...props}>{children.map((child) => renderChild(child))}</Tag>
      );
    }
  ),
  dataType: TRecord({
    tag: withSystem(
      {label: 'Tag', isBindable: true},
      TTypeConstructor(qualify(pluginName('Basic'), typeName('TagType')))
    ),
    attributes: attributesType,
    children: withSystem({label: 'Text', isBindable: true}, TList(TElement)),
  }),
  icon: {
    type: 'Component',
    value: qualify(pluginName('Builtin'), iconName('CustomElement')),
  },
  displayName: {
    type: 'Derived',
    defaultValue: 'Custom Element',
    getFromElement(element: ExpressionElement) {
      const data = Expressions.getElementData(element);
      const fields = Expressions.getRecord(data);
      if (!fields) return 'Custom Element';
      const tagExpr = fields.tag;
      if (!tagExpr) return 'Custom Element';
      const tag = Expressions.getText(tagExpr);
      return tag ? startCase(tag) : 'Custom Element';
    },
  },
  showEmpty: true,
});

const DangerousDOMForTest = Atom.create({
  render: Atom.createRender(
    ({data, generateAttributes, attributes, classes}) => {
      // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
      const Tag = data.tag;

      if (Tag === undefined) {
        throw new Error(
          'Builtin DangerousDOM `tag` is undefined. Make sure to pass in a valid `tag` to the DangerousDOM‘s `data` Record.'
        );
      }

      const props = {
        ...generateAttributes(attributes, classes),
        // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
        ...data.attributes.reduce(attrsDOMReducer, {}),
        // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
        dangerouslySetInnerHTML: data.value ? {__html: data.value} : null,
      };

      return <Tag {...props} />;
    }
  ),
  dataType: TRecord({
    /** HTML tag to render, eg <script> */
    tag: TText,
    /** This is the innerHTML value to set dangerously */
    value: TText,
    /** List of HTML attributes to render on the element */
    attributes: attributesType,
  }),
  showEmpty: false,
});

const precisionType = withSystem(
  {defaultValue: -1},
  TTypeConstructor(
    qualify(pluginName('Builtin'), typeName('DecimalNumberSelector'))
  )
);

// We need to clamp the precision before passing the value into `toFixed`
// because we may have expressions where the precision is set to greater
// than 8. Moreover, `toFixed` throws if called with number greater than 100.
function clampPrecisionArg(precision: number): number {
  return clamp(0, 8, precision);
}

// Common util between all the transformer functions for Number
// that will make sure to correctly handle the available precision decimal
// values. Here a value of `-1` refers to the "any" precision. I.e. the
// precision is "trim all trailing zeros but show all significant digits"
const numberToPrecision = (decimals: unknown, num: number): string => {
  if (typeof decimals !== 'number' || decimals < 0) {
    return String(num);
  }

  return num.toFixed(clampPrecisionArg(Math.floor(decimals)));
};

export const BuiltinFactory = (
  DOM: AtomConfig = Dummy,
  DangerousDOM: AtomConfig = Dummy
) =>
  Plugins.PrivilegedPlugin({
    atoms: {
      DOM,
      DangerousDOM,
    },
    dataTypes: {
      /**
       * Type of functions
       *
       * The example below represents the type of a function that takes
       * Text and returns Number (`Text => Number`):
       *
       * ```
       * {
       *   type: 'TypeApplication',
       *   val: {
       *     con: {
       *       type: 'TypeApplication',
       *       val: {
       *         con: {
       *           type: 'TypeConstructor',
       *           val: ['Builtin', 'Function']
       *         },
       *         arg: {
       *           type: 'TypeConstructor',
       *           val: ['Builtin', 'Text']
       *         }
       *       }
       *     },
       *     arg: {
       *       type: 'TypeConstructor',
       *       val: ['Builtin', 'Number']
       *     }
       *   }
       * }
       * ```
       */
      Function: TypeImplementation.Custom({
        params: ['Argument', 'Return'],
        parseValue: (_) =>
          Result.Error(
            'Cannot use Literal expressions to create Function values.'
          ),
        render: () => null,
      }),
      Children: typeWithDefaultEmptyArray,
      List: TypeImplementation.Custom({
        params: ['Item'],
        parseValue: (_) =>
          Result.Error('Cannot use Literal expressions to create List values.'),
        render: noRender,
        icon: TypeImplementation.IconByName(
          qualify(pluginName('Builtin'), iconName('List'))
        ),
        system: {defaultValue: []},
      }),
      Union: TypeImplementation.Custom({
        params: ['Variants'],
        parseValue: (_) =>
          Result.Error(
            'Cannot use Literal expressions to create Union values.'
          ),
        render: noRender,
        icon: TypeImplementation.IconByName(
          qualify(pluginName('Basic'), iconName('Enum'))
        ),
      }),
      Record: TypeImplementation.Custom({
        params: ['Fields'],
        parseValue: (_) =>
          Result.Error(
            'Cannot use Literal expressions to create Record values.'
          ),
        render: noRender,
        // TODO: Use a better icon for records, maybe a pair of curlies?
        icon: TypeImplementation.IconByName(
          qualify(pluginName('Builtin'), iconName('Record'))
        ),
      }),
      DecimalNumberSelector: DecimalNumberSelectorType,
      ...PrimitiveTypes,
    },
    icons: {
      CustomElement: CustomElementIcon,
      // @ts-expect-error - TS2322
      CustomElementAddPanel: CustomMElementAddPanelIcon,
      List: ListIcon,
      Record: RecordIcon,
    },
    values: {
      numberToText: {
        envType: {
          dataType: withSystem(
            {label: 'Format'},
            functionOf(
              precisionType,
              functionOf(
                withSystem({label: 'Number', isBindable: true}, TNumber),
                withSystem({label: 'Text'}, TText)
              )
            )
          ),
          exported: 'visible',
          implicit: 'prefilledArgs',
        },
        value:
          (decimals: unknown) =>
          (num: unknown): string => {
            if (typeof num !== 'number') {
              return '';
            }
            return numberToPrecision(decimals, num);
          },
      },
      numberToListOfElements: {
        envType: {
          dataType: withSystem(
            {label: 'Format'},
            functionOf(
              precisionType,
              functionOf(
                withSystem({label: 'Number', isBindable: true}, TNumber),
                withSystem({label: 'Text'}, TListOfElement)
              )
            )
          ),
          exported: 'visible',
          implicit: 'prefilledArgs',
        },
        value: (decimals: unknown) => (num: unknown) => {
          if (typeof num !== 'number') {
            return [];
          }

          const numString = numberToPrecision(decimals, num);

          return [
            {
              text: true,
              type: null,
              id: 'temp',
              children: [],
              data: {value: numString},
            },
          ];
        },
      },
      // textToListOfElements: {
      //   envType: {
      //     implicit: 'prefilledArgs',
      //     dataType: ({
      //       ...functionOf(
      //         withSystem({label: 'Plain Text', isBindable: true}, TText),
      //         {type: 'List', of: {type: 'Element'}}
      //       ),
      //       system: {label: 'Text to Elements'},
      //     }: TypeApplication),
      //   },
      //   // IIFE here to encapsulate everything:
      //   value: (() => {
      //     const BASE_ID = 'auto-generated-id-0';
      //     const NON_BREAKING_SPACE = '\xa0';
      //     const replaceAllSpaces = spaces =>
      //       spaces.replace(/ /g, NON_BREAKING_SPACE);
      //     function getListOfNodesFromString(string: string) {
      //       // Here we replace some spaces with non-breaking spaces
      //       const chunks = string
      //         // Case 1: Whenever we have more than one space in a row, we need them to be
      //         // non-breaking spaces:
      //         .replace(/  +/g, replaceAllSpaces)
      //         // Case 2: Whenever we have a space at the beginning of a line, we need it
      //         // to be a non-breaking space:
      //         //
      //         // NOTE: Order of these matter! If we were to run this replacement first,
      //         // the first two spaces of a line will not be reflected as non-breaking.
      //         .replace(/^ /g, NON_BREAKING_SPACE)
      //         .replace(/\n /g, '\n' + NON_BREAKING_SPACE)
      //         .split('\n');
      //       let currentId = BASE_ID;
      //       const result = [];
      //       for (let i = 0; i < chunks.length; i += 1) {
      //         const value = chunks[i];
      //         if (value !== '') {
      //           result.push({
      //             text: true,
      //             type: null,
      //             id: currentId,
      //             children: [],
      //             data: {value},
      //           });
      //           currentId = incrementGuid(currentId);
      //         }
      //
      //         if (i !== chunks.length - 1) {
      //           result.push({
      //             text: false,
      //             type: 'LineBreak',
      //             id: currentId,
      //             data: {},
      //             children: [],
      //           });
      //           currentId = incrementGuid(currentId);
      //         }
      //       }
      //
      //       return result;
      //     }
      //
      //     return getListOfNodesFromString;
      //   })(),
      // },
    },
  });

export const BuiltinFactoryTest = BuiltinFactory.bind(
  null,
  DOMForTest,
  DangerousDOMForTest
);
